import { TranslationContext } from 'contexts/translation-context';
import { useEffect, useState, useContext } from 'react';
import styled from 'styled-components';
import { formatFloatWithCommasAndDecimals } from 'utils/new-formats';
import type { PackagePrices, PromoCodeDiscount } from 'types/join';
import { useSelector } from 'react-redux';
import { calculatePromoCodeDiscount } from 'utils/promo-code-helper';
import { calculateTotalPrice, getAddonDisplayName } from 'utils/package-helper';
import { getPlanName } from './formats';
import { PromoCode } from './promo-code';
import type { PlanState } from '../reducers/plan';
import Icon from 'components/common/icon/icon';

const Container = styled.div`
  height: max-content;
  border: 1px solid ${props => props.theme.colors.red};
  border-radius: 5px;
  display: flex;
  flex-direction: column;
`;
const PlansContainer = styled.div`
  height: max-content;
  padding: 10px;
`;
const TotalContainer = styled.div`
  height: 37px;
  color: ${props => props.theme.colors.white};
  background-color: ${props => props.theme.colors.red};
  padding: 10px;
  font-family: ${props => props.theme.fonts.montserratBold};
  font-size: 16px;
  display: flex;
  justify-content: space-between;
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 4px;
`;
const Text = styled.div`
  font-family: ${props => props.theme.fonts.montserratMedium};
  color: ${props => props.theme.colors.siteBlack};
  font-size: 16px;
`;
const Title = styled.div`
  text-align: center;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.montserratBold};
  margin-bottom: 10px;
`;

const DiscountContainer = styled.div`
  margin-top: 10px;
`;

const DiscountRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 4px;
  align-items: center;
`;

const DiscountIcon = styled(Icon)`
  margin-right: 8px;
`;

const DiscountText = styled.div`
  font-family: ${props => props.theme.fonts.montserratMedium};
  color: ${props => props.theme.colors.lightGray};
  font-size: 14px;
  display: flex;
  align-items: center;
  flex: 1;
`;

const DiscountAmount = styled.div`
  font-family: ${props => props.theme.fonts.montserratMedium};
  color: ${props => props.theme.colors.lightGray};
  font-size: 14px;
`;

export const OrderSummary = ({
  selectedPlan,
  selectedAddons,
  onPromoCodeApplied,
  userCountry,
  setFinalTotalPrice,
  finalTotalPrice,
  isActive = true
}: {
  selectedPlan: PackagePrices | undefined;
  selectedAddons: (PackagePrices | undefined)[] | undefined;
  onPromoCodeApplied: (promoCodeId: string) => void;
  userCountry?: string;
  setFinalTotalPrice: (total: number) => void;
  finalTotalPrice: number;
  isActive?: boolean;
}) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const [discount, setDiscount] = useState<PromoCodeDiscount | null>(null);

  const { promoCode: promoCodeState } = useSelector((state: { plan: PlanState }) => state.plan);

  useEffect(() => {
    if (promoCodeState.data && promoCodeState.data.isValid && promoCodeState.data.promoCode) {
      const calculatedDiscount = calculatePromoCodeDiscount(
        promoCodeState.data.promoCode,
        selectedPlan,
        selectedAddons,
        userCountry || ''
      );

      if (calculatedDiscount) {
        setDiscount(calculatedDiscount);
      } else {
        setDiscount(null);
      }
    } else {
      setDiscount(null);
    }
  }, [promoCodeState.data, selectedPlan, selectedAddons, userCountry]);

  useEffect(() => {
    const baseTotal = calculateTotalPrice(selectedPlan, selectedAddons);
    const finalTotal = baseTotal - (discount?.discountAmount || 0);
    setFinalTotalPrice(finalTotal);
  }, [selectedPlan, selectedAddons, discount?.discountAmount, setFinalTotalPrice]);

  return (
    <>
      <Title>{TRANSLATION_TEXT.order_summary}</Title>
      {((selectedPlan && selectedPlan?.period?.toLowerCase().includes('year')) ||
        promoCodeState.data?.promoCode) && (
        <PromoCode
          onPromoCodeApplied={onPromoCodeApplied}
          selectedPlan={selectedPlan}
          selectedAddons={selectedAddons}
          userCountry={userCountry}
          isActive={isActive}
        />
      )}
      <Container>
        <PlansContainer>
          <Row>
            <Text>{getPlanName(selectedPlan?.packageName)}</Text>
            <Text>${formatFloatWithCommasAndDecimals(selectedPlan?.price ?? 0)}</Text>
          </Row>
          {selectedAddons?.map(
            (addon, i) =>
              selectedAddons[i - 1]?.packageName !== addon?.packageName && (
                <Row key={addon?.packageName}>
                  <Text>{getAddonDisplayName(addon, selectedAddons)}</Text>
                  <Text>
                    $
                    {addon?.price !== 0
                      ? formatFloatWithCommasAndDecimals(
                          (addon && addon.price * addon.addonCount) ?? 0
                        )
                      : 0}
                  </Text>
                </Row>
              )
          )}
          {discount && (
            <DiscountContainer>
              <DiscountRow>
                <DiscountText>
                  <DiscountIcon name="tagIcon" />
                  {TRANSLATION_TEXT.promo_code_discount}
                </DiscountText>
                <DiscountAmount>
                  -${formatFloatWithCommasAndDecimals(discount.discountAmount)}
                </DiscountAmount>
              </DiscountRow>
              <DiscountRow style={{ paddingLeft: '20px' }}>({discount.promoCodeName})</DiscountRow>
            </DiscountContainer>
          )}
        </PlansContainer>
        <TotalContainer>
          <div>{TRANSLATION_TEXT.total}</div>
          <div>${formatFloatWithCommasAndDecimals(finalTotalPrice)}</div>
        </TotalContainer>
      </Container>
    </>
  );
};
